import { isoToScreen, type Point } from "./iso";

/**
 * The garden plot as a FREE-FORM SET OF SQUARE CELLS (replaces the old single
 * radius). Each cell is `CELL` garden-units square. The starting garden is one
 * cell centered on the origin. Players expand by adding rows/columns of cells on
 * a side (N/S/E/W); the model supports arbitrary shapes for future per-edge
 * expansion. Roam bounds, ground rendering, and placement all derive from the
 * union of cells.
 *
 * Cell coordinates (cx, cy) are integer grid indices. Cell (0,0) is the start.
 * A cell's center in garden units is (cx*CELL, cy*CELL).
 */

/** Size of one cell in garden units (the starting area's span). */
export const CELL = 7;

export interface Cell {
  cx: number;
  cy: number;
}

/** Serializable plot: the list of owned cells. */
export interface Plot {
  cells: Cell[];
}

export function startingPlot(): Plot {
  return { cells: [{ cx: 0, cy: 0 }] };
}

export function hasCell(plot: Plot, cx: number, cy: number): boolean {
  return plot.cells.some((c) => c.cx === cx && c.cy === cy);
}

/** Integer grid bounds of the plot (inclusive). */
export function cellBounds(plot: Plot): { minX: number; maxX: number; minY: number; maxY: number } {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const c of plot.cells) {
    minX = Math.min(minX, c.cx);
    maxX = Math.max(maxX, c.cx);
    minY = Math.min(minY, c.cy);
    maxY = Math.max(maxY, c.cy);
  }
  return { minX, maxX, minY, maxY };
}

/** Garden-unit center of the whole plot (for camera home / origin). */
export function plotCenter(plot: Plot): Point {
  const b = cellBounds(plot);
  return {
    x: ((b.minX + b.maxX) / 2) * CELL,
    y: ((b.minY + b.maxY) / 2) * CELL,
  };
}

/**
 * Is garden-unit point (gx, gy) inside the plot's UNION of cells?
 *
 * The cells tile seamlessly, so the margin must only inset the OUTER boundary —
 * not internal seams between adjacent cells (otherwise items/creatures can never
 * sit on a boundary line, and roamers get trapped per-cell). So: the point must
 * be inside some full cell box, AND at least `margin` away from any EXPOSED edge
 * (a cell side that has no neighbour). Edges shared with a neighbour are free.
 */
export function inPlot(plot: Plot, gx: number, gy: number, margin = 0): boolean {
  const half = CELL / 2;
  // Find the cell whose box contains the point (full extent, no margin).
  const cell = plot.cells.find((c) => {
    const px = c.cx * CELL, py = c.cy * CELL;
    return gx >= px - half && gx <= px + half && gy >= py - half && gy <= py + half;
  });
  if (!cell) return false;
  if (margin <= 0) return true;

  // Local position within the cell (−half … +half on each axis).
  const lx = gx - cell.cx * CELL;
  const ly = gy - cell.cy * CELL;
  // For each side, only enforce the margin if there's NO neighbouring cell.
  if (!hasCell(plot, cell.cx - 1, cell.cy) && lx < -half + margin) return false; // west edge
  if (!hasCell(plot, cell.cx + 1, cell.cy) && lx > half - margin) return false; // east edge
  if (!hasCell(plot, cell.cx, cell.cy - 1) && ly < -half + margin) return false; // north edge
  if (!hasCell(plot, cell.cx, cell.cy + 1) && ly > half - margin) return false; // south edge
  return true;
}

/**
 * Clamp a point to the nearest in-plot position. Only pulls a point back in when
 * it's genuinely OUTSIDE the union (so creatures crossing internal seams aren't
 * snapped). Clamps to the nearest cell box; the per-cell box is fine here because
 * we only act when already outside every cell.
 */
export function clampToPlot(plot: Plot, gx: number, gy: number, margin = 0): Point {
  if (inPlot(plot, gx, gy, margin)) return { x: gx, y: gy };
  let best: Point = { x: gx, y: gy };
  let bestD = Infinity;
  const half = CELL / 2 - margin;
  for (const c of plot.cells) {
    const px = c.cx * CELL;
    const py = c.cy * CELL;
    const qx = Math.max(px - half, Math.min(px + half, gx));
    const qy = Math.max(py - half, Math.min(py + half, gy));
    const d = (qx - gx) ** 2 + (qy - gy) ** 2;
    if (d < bestD) {
      bestD = d;
      best = { x: qx, y: qy };
    }
  }
  return best;
}

/** A random in-plot point (uniform across cells). rand() returns [0,1). */
export function randomInPlot(plot: Plot, rand: () => number, margin = 0): Point {
  const c = plot.cells[Math.floor(rand() * plot.cells.length)]!;
  const half = CELL / 2 - margin;
  return {
    x: c.cx * CELL + (rand() * 2 - 1) * half,
    y: c.cy * CELL + (rand() * 2 - 1) * half,
  };
}

export type Side = "n" | "s" | "e" | "w";

const DELTA: Record<Side, [number, number]> = {
  // iso/garden space: -y north/back, +y south/front, +x east, -x west
  n: [0, -1],
  s: [0, 1],
  e: [1, 0],
  w: [-1, 0],
};

/**
 * Every EXPOSED edge of the plot: for each owned cell, each side that has no
 * neighbour is a place a new cell could be added. Returns the target cell (the
 * empty neighbour) and the garden-space point to put the + button.
 *
 * Note: two cells could share the same empty target (e.g. an inner corner); we
 * dedupe by target so each addable cell gets exactly one button.
 */
export interface EdgeHandle {
  /** The cell that would be added. */
  target: Cell;
  /** Garden-space position for the + button (just outside the owning edge). */
  gx: number;
  gy: number;
}

export function edgeHandles(plot: Plot): EdgeHandle[] {
  const seen = new Set<string>();
  const out: EdgeHandle[] = [];
  for (const c of plot.cells) {
    for (const side of ["n", "s", "e", "w"] as Side[]) {
      const [dx, dy] = DELTA[side];
      const tx = c.cx + dx;
      const ty = c.cy + dy;
      if (hasCell(plot, tx, ty)) continue; // neighbour exists — not an edge
      const k = `${tx},${ty}`;
      if (seen.has(k)) continue;
      seen.add(k);
      // Handle sits at the midpoint between the owning cell and the new cell.
      out.push({ target: { cx: tx, cy: ty }, gx: (c.cx + tx) / 2 * CELL, gy: (c.cy + ty) / 2 * CELL });
    }
  }
  return out;
}

/** Add a specific cell (by grid index) to the plot. */
export function addCell(plot: Plot, cx: number, cy: number): Plot {
  if (hasCell(plot, cx, cy)) return plot;
  return { cells: [...plot.cells, { cx, cy }] };
}

/** Remove a specific cell (by grid index) from the plot. No-op if it's the last cell. */
export function removeCell(plot: Plot, cx: number, cy: number): Plot {
  if (plot.cells.length <= 1) return plot;
  return { cells: plot.cells.filter((c) => !(c.cx === cx && c.cy === cy)) };
}

/** Are all the plot's cells 4-connected (one contiguous region)? Used so selling a cell can't leave an
 *  island floating off on its own. An empty/single-cell plot is trivially connected. */
export function isConnected(plot: Plot): boolean {
  if (plot.cells.length <= 1) return true;
  const key = (cx: number, cy: number) => `${cx},${cy}`;
  const all = new Set(plot.cells.map((c) => key(c.cx, c.cy)));
  const seen = new Set<string>();
  const start = plot.cells[0]!;
  const stack: Cell[] = [start];
  seen.add(key(start.cx, start.cy));
  while (stack.length) {
    const c = stack.pop()!;
    for (const [dx, dy] of Object.values(DELTA)) {
      const nx = c.cx + dx, ny = c.cy + dy, k = key(nx, ny);
      if (all.has(k) && !seen.has(k)) { seen.add(k); stack.push({ cx: nx, cy: ny }); }
    }
  }
  return seen.size === all.size;
}

/** Can this cell be SOLD (removed) without orphaning the plot? False for the origin (0,0) starter cell,
 *  the last remaining cell, or any cell whose removal would split the plot into disconnected pieces. This
 *  is the STRUCTURAL check only — the caller separately verifies the cell's area is clear of items. */
export function canSellCell(plot: Plot, cx: number, cy: number): boolean {
  if (cx === 0 && cy === 0) return false;         // the free starter cell stays
  if (!hasCell(plot, cx, cy)) return false;
  if (plot.cells.length <= 1) return false;
  // INTERIOR cells (owned neighbours on all 4 sides) can't be sold — you'd leave a hole in the plot.
  if (hasCell(plot, cx + 1, cy) && hasCell(plot, cx - 1, cy) && hasCell(plot, cx, cy + 1) && hasCell(plot, cx, cy - 1)) return false;
  return isConnected(removeCell(plot, cx, cy));
}

/** The garden-unit bounds (inclusive of the outer edge) of a cell's square region. Used to test whether
 *  anything (plant/decor/soil/water) sits within a cell before selling it. */
export function cellRegion(cx: number, cy: number): { gx0: number; gy0: number; gx1: number; gy1: number } {
  const half = CELL / 2;
  return { gx0: cx * CELL - half, gy0: cy * CELL - half, gx1: cx * CELL + half, gy1: cy * CELL + half };
}

/** Which grid cell (cx,cy) contains garden-unit point (gx,gy)? (Nearest cell centre on the CELL grid.) */
export function cellAtPoint(gx: number, gy: number): Cell {
  return { cx: Math.round(gx / CELL), cy: Math.round(gy / CELL) };
}

/** Screen position (pre-origin) of a cell's center. */
export function cellScreen(c: Cell): Point {
  return isoToScreen(c.cx * CELL, c.cy * CELL);
}

/* ---- Slot sub-grid ----
 * A STATIC sub-grid spanning the whole plot: each cell is divided into SOIL_DIV × SOIL_DIV slots.
 * Fences (sim/fence.ts) run along its slot edges. */

export const SOIL_DIV = 3;
/** Garden-unit size of one slot. */
export const SOIL_SLOT = CELL / SOIL_DIV;
